import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { $crud } from "../../helper/CrudFactory";
import LanguageContext from "../../helper/LanguageContext";
import {
  cancelScheduledSwitch,
  cancelSubscription,
  fetchAndStoreSubscriptionStatus,
  getPlans,
  getKjuupIoFeatures,
  getMarketingPrices,
  labelForKjuupIoFeature,
  resumeSubscription,
  startTrial,
  subscribe,
  confirmSetup,
  switchPlan,
} from "../../services/PaymentService";
import { SET_SUBSCRIPTION_STATUS } from "../../store";
import { isBillingNetworkError, normalizeSubscriptionStatus } from "../../services/PaymentService";
import { BASE_URL } from "../../helper/constants";
import { openDirectChat } from "../Cockpit/cockpitNavigation";
import PackageTile from "../../components/PackageTile";
import appConfig from "../../config/appConfig";
import {
  companyTrialEndedAskBody,
  formatCompanyAdminNames,
  isCompanyAdminWithSeat,
  navigateToKjuupIoBilling,
  shouldShowCompanyTrialEndedNote,
  shouldShowCompanyTrialUpgradeNotes,
} from "../../utils/kjuupIoBillingNav";
import { isConnectedToKjuupPlus, resolvePackageId } from "../../utils/packageTheme";
import { resolveStripeFlags } from "../../utils/stripeFeature";
import TrialCountdownText from "../../components/TrialCountdownText";
import KjuupIoSetupModal from "../../components/KjuupIoSetupModal";
import "./SubscriptionPage.css";

export const DEFAULT_ANNUAL_SAVE_PCT = 10;
// Fallback only — the trial length comes from the status payload (Admin Center setting).
export const DEFAULT_TRIAL_DAYS = 30;
const CURRENCY_SYMBOLS = { eur: "€", usd: "$", gbp: "£", inr: "₹" };
export const PLAN_MONTHLY = "plus_monthly";
export const PLAN_YEARLY = "plus_yearly";
const PREFERRED_PLAN_KEY = "kjuup_io_preferred_plan";

export const t = (strings, key, vars, fallback) => {
  const template = strings[key] || fallback || key;
  if (!vars) return template;
  return Object.keys(vars).reduce(
    (out, k) => out.replace(new RegExp(`\\{${k}\\}`, "g"), String(vars[k])),
    template
  );
};

/** Like t(), but placeholder values may be React nodes (a struck-through price). */
export const tNode = (strings, key, vars, fallback) => {
  const template = strings[key] || fallback || key;
  return template
    .split(/(\{\w+\})/g)
    .filter(Boolean)
    .map((part, i) => {
      const m = part.match(/^\{(\w+)\}$/);
      return m && vars && m[1] in vars ? (
        <React.Fragment key={i}>{vars[m[1]]}</React.Fragment>
      ) : (
        part
      );
    });
};

// Net price after the company + personal discount. `listAmount` keeps the
// price before it for the struck-through label; every other label (pills,
// switch modal, yearly savings) reads `amount` and so shows what is charged.
export const applyDiscount = (byPlan, percent) => {
  if (!(percent > 0)) return byPlan;
  const out = {};
  Object.keys(byPlan).forEach((key) => {
    const plan = byPlan[key];
    out[key] =
      plan && plan.amount != null
        ? { ...plan, listAmount: plan.amount, amount: Math.round(plan.amount * (1 - percent / 100)) }
        : plan;
  });
  return out;
};

export const listPlan = (plan) =>
  plan && plan.listAmount != null ? { ...plan, amount: plan.listAmount } : null;

const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(".", ",");
};

// "~~€29,–~~ €11,60" when discounted, otherwise just the price.
export const DiscountedPrice = ({ net, list }) =>
  list ? (
    <>
      <span className="kjuup-sub-price-strike">{list}</span>{" "}
      <span className="kjuup-sub-price-net">{net}</span>
    </>
  ) : (
    net
  );

/** "Your discount: 60% off · Acme: 20% · Personal: 50%" (breakdown only when both apply). */
export const discountSummaryText = (strings, discount) => {
  if (!discount || !(Number(discount.percent) > 0)) return "";
  const parts = [
    t(strings, "kjuup_io_discount_total", { percent: formatPercent(discount.percent) }, "Your discount: {percent}% off"),
  ];
  if (Number(discount.companyPercent) > 0 && Number(discount.userPercent) > 0) {
    parts.push(
      t(
        strings,
        "kjuup_io_discount_company",
        { company: discount.companyName || "—", percent: formatPercent(discount.companyPercent) },
        "{company}: {percent}%"
      ),
      t(strings, "kjuup_io_discount_personal", { percent: formatPercent(discount.userPercent) }, "Personal: {percent}%")
    );
  }
  return parts.join(" · ");
};

const otherPlan = (key) => (key === PLAN_YEARLY ? PLAN_MONTHLY : PLAN_YEARLY);
const symbolFor = (cur) => CURRENCY_SYMBOLS[cur] || (cur ? `${String(cur).toUpperCase()} ` : "");

/** Display money like €17,– (whole) or €17,08 (fractional). */
const currencyFormatter = (amountCents, currency) => {
  if (amountCents == null) return "";
  const amount = amountCents / 100;
  const symbol = symbolFor(currency);
  if (Number.isInteger(amount)) return `${symbol}${amount},–`;
  return `${symbol}${amount.toFixed(2).replace(".", ",")}`;
};

export const priceMajor = (plan) => {
  if (!plan || plan.amount == null) return "";
  return currencyFormatter(plan.amount, plan.currency);
};

/** Monthly equivalent for a yearly plan (cents → display). */
export const yearlyEquivalentMajor = (yearlyPlan) => {
  if (!yearlyPlan || yearlyPlan.amount == null) return "";
  const monthlyCents = Math.round(yearlyPlan.amount / 12);
  return currencyFormatter(monthlyCents, yearlyPlan.currency);
};

export const derivePricing = (priceByPlan, selectedPlan, annualSavePct) => {
  const monthlyPlan = priceByPlan?.[PLAN_MONTHLY] || null;
  const yearlyPlan = priceByPlan?.[PLAN_YEARLY] || null;
  const billingPeriod = selectedPlan === PLAN_YEARLY ? "yearly" : "monthly";
  const monthlyPrice = monthlyPlan ? priceMajor(monthlyPlan) : "";
  const yearlyPrice = yearlyPlan ? priceMajor(yearlyPlan) : "";
  const yearlyEquivalent = yearlyPlan ? yearlyEquivalentMajor(yearlyPlan) : "";
  const discountPercentage = annualSavePct;
  let yearlySavingsCents = null;
  if (monthlyPlan?.amount != null && yearlyPlan?.amount != null) {
    const fullYearMonthly = monthlyPlan.amount * 12;
    const saved = fullYearMonthly - yearlyPlan.amount;
    if (saved > 0) yearlySavingsCents = saved;
  }
  const yearlySavings = yearlySavingsCents != null
    ? currencyFormatter(yearlySavingsCents, yearlyPlan?.currency || monthlyPlan?.currency)
    : "";
  return {
    billingPeriod,
    monthlyPlan,
    yearlyPlan,
    monthlyPrice,
    yearlyPrice,
    yearlyEquivalent,
    yearlySavings,
    yearlySavingsCents,
    discountPercentage,
    selectedPlan,
    currency: (billingPeriod === "yearly" ? yearlyPlan : monthlyPlan)?.currency || "eur",
  };
};

const perUnit = (plan) => {
  if (!plan || !plan.interval) return "";
  return plan.intervalCount && plan.intervalCount > 1
    ? `${plan.intervalCount} ${plan.interval}s`
    : plan.interval;
};

const formatPrice = (plan) => {
  if (!plan || plan.amount == null) return "";
  const per = perUnit(plan);
  return per ? `${priceMajor(plan)} / ${per}` : priceMajor(plan);
};

const compactPrice = (plan) => {
  if (!plan || plan.amount == null) return "";
  const unit = plan.interval === "year" ? "yr" : plan.interval === "month" ? "mo" : plan.interval;
  return `${priceMajor(plan)}/${unit}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (e) {
    return String(value);
  }
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const readPreferredPlan = () => {
  try {
    const stored = localStorage.getItem(PREFERRED_PLAN_KEY);
    if (stored === PLAN_MONTHLY || stored === PLAN_YEARLY) return stored;
  } catch (e) {
    /* ignore */
  }
  return PLAN_MONTHLY;
};

export const persistPreferredPlan = (plan) => {
  try {
    localStorage.setItem(PREFERRED_PLAN_KEY, plan);
  } catch (e) {
    /* ignore */
  }
};

const fillStr = (template, vars) =>
  String(template || "").replace(/\{\{(\w+)\}\}/g, (match, key) => (key in vars ? String(vars[key]) : match));

/**
 * One package of the Plans page (the app's PlanCard): icon tile in the package
 * colour, name and tagline, "Current" on the user's package, the feature
 * checks, then the package's own content.
 */
function PlanCard({ id, label, tag, current, currentLabel, features = [], children }) {
  return (
    <section className={`kj-plan-card kj-plan-card--${id}${current ? " is-current" : ""}`} aria-label={label}>
      <div className="kj-plan-card__head">
        <PackageTile pkg={id} size="lg" />
        <div className="kj-plan-card__titles">
          <h3 className="kj-plan-card__name">{label}</h3>
          {tag ? <p className="kj-plan-card__tag">{tag}</p> : null}
        </div>
        {current ? <span className="kj-plan-card__current">{currentLabel}</span> : null}
      </div>
      {features.length ? (
        <ul className="kj-plan-card__checks">
          {features.map((feature, index) => (
            <li key={`${index}-${feature}`}>
              <span className="material-symbols-outlined" aria-hidden="true">check</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {children}
    </section>
  );
}

/** Monthly / Yearly side by side; a tile without onClick only shows its state. */
function PlanOptions({ label, options }) {
  return (
    <div className="kj-plan-options" role="radiogroup" aria-label={label}>
      {options.map((option) => {
        const className = `kj-plan-option${option.selected ? " is-selected" : ""}`;
        const content = (
          <>
            <span className="kj-plan-option__head">
              <span className="kj-plan-option__label">{option.label}</span>
              {option.selected ? (
                <span className="material-symbols-outlined kj-plan-option__check" aria-hidden="true">
                  check_circle
                </span>
              ) : null}
            </span>
            {option.price ? <span className="kj-plan-option__price">{option.price}</span> : null}
          </>
        );
        return option.onClick ? (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={!!option.selected}
            className={className}
            disabled={option.disabled}
            onClick={option.onClick}
          >
            {content}
          </button>
        ) : (
          <div key={option.key} role="radio" aria-checked={!!option.selected} aria-disabled="true" className={className}>
            {content}
          </div>
        );
      })}
    </div>
  );
}

const adminLabel = (admin) =>
  [admin && admin.firstName, admin && admin.lastName].filter(Boolean).join(" ").trim() ||
  (admin && admin.name) ||
  "";

function AdminAvatar({ admin }) {
  const src = admin && admin.avatar;
  if (src) {
    const url = String(src).startsWith("http") ? src : BASE_URL + src;
    return <img className="kjuup-sub-admin-avatar" src={url} alt="" />;
  }
  const initials =
    [admin && admin.firstName, admin && admin.lastName]
      .filter(Boolean)
      .map((part) => String(part).charAt(0).toUpperCase())
      .join("") || String(adminLabel(admin) || "?").charAt(0).toUpperCase();
  return <span className="kjuup-sub-admin-avatar">{initials}</span>;
}

/** Renders first-payment / renewal date when backend provides it; otherwise null (ready for wiring). */
export function CheckoutForm({ intentType, onSuccess, onCancel, strings, processing, setProcessing, ctaLabel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || processing) return;
    setProcessing(true);
    setErrorMsg("");
    try {
      const confirmFn =
        intentType === "setup"
          ? stripe.confirmSetup.bind(stripe)
          : stripe.confirmPayment.bind(stripe);
      const { error } = await confirmFn({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/subscription`,
        },
        redirect: "if_required",
      });
      if (error) {
        if (error.type !== "validation_error") {
          setErrorMsg(error.message || strings.kjuup_io_toast_payment_failed);
          $crud.notify({
            type: "error",
            message: error.message || strings.kjuup_io_toast_payment_failed,
          });
        } else {
          setErrorMsg(error.message || "");
        }
        setProcessing(false);
        return;
      }
      await onSuccess();
    } catch (err) {
      setErrorMsg(err.message || strings.kjuup_io_toast_payment_failed);
      setProcessing(false);
    }
  };

  return (
    <form className="kjuup-sub-payment-form" onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMsg ? <div className="kjuup-sub-payment-error">{errorMsg}</div> : null}
      <button
        type="submit"
        className="kjuup-sub-gradient-btn kjuup-sub-gradient-btn--form"
        disabled={!stripe || !elements || processing}
      >
        <span className="btn-inner">
          {processing ? "…" : ctaLabel || strings.kjuup_io_subscribe_now || "Subscribe now"}
        </span>
      </button>
      <button type="button" className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--block" onClick={onCancel} disabled={processing}>
        {strings.kjuup_io_not_now || "Not now"}
      </button>
    </form>
  );
}

export default function SubscriptionPage() {
  const dispatch = useDispatch();
  const history = useHistory();
  const { strings, language, isGerm } = useContext(LanguageContext);
  const tr = (key, de, en) => strings[key] || (isGerm ? de : en);
  const profile = useSelector((state) => state.profile);
  const subscription = useSelector((state) => state.subscription) || {};
  const { stripeUiEnabled, isKjuupPlusMember } = resolveStripeFlags(
    profile,
    subscription
  );
  const isActive = !!subscription.active;
  const isTrialing = !!subscription.trialing;
  const canStartTrial = !!subscription.canStartTrial;
  const hasPaymentMethod = !!subscription.hasPaymentMethod;
  const companyBilling = subscription.companyBilling;
  const activityMessages = useSelector((state) => state.activityMessages);

  const goBack = useCallback(() => {
    // Opened by deep link or after a reload there is nothing to go back to.
    if (history.length > 1) {
      history.goBack();
      return;
    }
    history.push("/cockpit");
  }, [history]);

  useEffect(() => {
    if (!stripeUiEnabled) {
      history.replace("/profile-setting");
    }
  }, [stripeUiEnabled, history]);

  const [selectedPlan, setSelectedPlan] = useState(readPreferredPlan);
  const [processing, setProcessing] = useState(false);
  const [screenLoading, setScreenLoading] = useState(true);
  // Kjuup.io workspace setup (chats / email mode) right after the first
  // trial or booking — once per user (kjuupIoSetupDone).
  const reduxUser = useSelector((state) => state.user);
  const [showIoSetup, setShowIoSetup] = useState(false);
  const openIoSetupIfNeeded = () => {
    if (reduxUser && reduxUser.kjuupIoSetupDone) return;
    setShowIoSetup(true);
  };
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [listPriceByPlan, setPriceByPlan] = useState({});
  const [ioFeatureRows, setIoFeatureRows] = useState([]);
  const [plusFeatureRows, setPlusFeatureRows] = useState([]);
  const [switchTarget, setSwitchTarget] = useState(null);
  const [checkout, setCheckout] = useState(null); // { clientSecret, intentType, stripePromise, billingStartsAfterTrial, setupIntentId, subscriptionId }

  const ioFeatureLabels = useMemo(
    () =>
      (ioFeatureRows || [])
        .map((f) => labelForKjuupIoFeature(f, language))
        .filter(Boolean),
    [ioFeatureRows, language]
  );

  const pkgId = resolvePackageId(profile, subscription);
  const plusMember = isConnectedToKjuupPlus(profile, subscription);
  // Kjuup+ card: the Admin Panel's feature list (as on kjuup.de/pakete), the static copy only when it gives none.
  const plusFeatures = useMemo(() => {
    const fromAdmin = (plusFeatureRows || []).map((f) => labelForKjuupIoFeature(f, language)).filter(Boolean);
    if (fromAdmin.length) return fromAdmin;
    return [
      tr("pro_settings_plan_plus_f1", "Alles aus Kjuup", "Everything in Kjuup"),
      tr("pro_settings_plan_plus_f2", "Firmenbeiträge und interne News", "Company articles and internal news"),
      tr("pro_settings_plan_plus_f3", "Firmendokumente", "Company documents"),
      tr("pro_settings_plan_plus_f4", "Admin-Center", "Admin center"),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plusFeatureRows, language, strings]);

  useEffect(() => {
    let cancelled = false;
    getMarketingPrices().then(
      (data) => {
        if (!cancelled) setPlusFeatureRows(data.kjuupPlusFeatures || []);
      },
      () => {}
    );
    return () => {
      cancelled = true;
    };
  }, []);

  const selectPlan = useCallback((planKey) => {
    if (planKey !== PLAN_MONTHLY && planKey !== PLAN_YEARLY) return;
    setSelectedPlan(planKey);
    persistPreferredPlan(planKey);
  }, []);

  const planLabel = useCallback(
    (key) => {
      if (key === PLAN_MONTHLY) return strings.kjuup_io_plan_monthly || "Monthly";
      if (key === PLAN_YEARLY) return strings.kjuup_io_plan_yearly || "Yearly";
      return key || "—";
    },
    [strings]
  );

  const discount = subscription.discount || null;
  const discountPercent = Number(discount && discount.percent) || 0;
  const priceByPlan = useMemo(
    () => applyDiscount(listPriceByPlan, discountPercent),
    [listPriceByPlan, discountPercent]
  );
  const discountText = discountSummaryText(strings, discount);

  const annualSavePct = useMemo(() => {
    const fromApi = priceByPlan?.[PLAN_YEARLY]?.savePercent;
    return fromApi != null && fromApi > 0 ? fromApi : DEFAULT_ANNUAL_SAVE_PCT;
  }, [priceByPlan]);

  const pricing = useMemo(
    () => derivePricing(priceByPlan, selectedPlan, annualSavePct),
    [priceByPlan, selectedPlan, annualSavePct]
  );

  const errorText = (e, fallback) =>
    isBillingNetworkError(e)
      ? strings.kjuup_io_request_timeout || "The connection timed out. Please try again."
      : e?.message || fallback;

  const refreshStatus = useCallback(async () => {
    return fetchAndStoreSubscriptionStatus(dispatch);
  }, [dispatch]);

  const applyPlans = useCallback((plans) => {
    if (!Array.isArray(plans)) return;
    const map = {};
    plans.forEach((p) => {
      if (p && p.key) map[p.key] = p;
    });
    setPriceByPlan(map);
  }, []);

  const loadPlans = useCallback(async () => {
    try {
      const plans = await getPlans();
      applyPlans(plans);
    } catch (e) {
      console.log("[subscription] loadPlans error:", e?.message);
    }
  }, [applyPlans]);

  const loadIoFeatures = useCallback(async () => {
    try {
      const features = await getKjuupIoFeatures();
      setIoFeatureRows(Array.isArray(features) ? features : []);
    } catch (e) {
      console.log("[subscription] loadIoFeatures error:", e?.message);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    setScreenLoading(true);
    (async () => {
      try {
        await Promise.all([refreshStatus(), loadPlans(), loadIoFeatures()]);
      } finally {
        if (!cancelled) setScreenLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshStatus, loadPlans, loadIoFeatures]);

  const refreshWithRetry = async (predicate) => {
    const ok = typeof predicate === "function" ? predicate : (d) => d && d.active;
    for (let i = 0; i < 5; i += 1) {
      const data = await fetchAndStoreSubscriptionStatus(dispatch);
      if (ok(data)) return true;
      await sleep(1500);
    }
    return false;
  };

  /** No-card free trial (one lifetime). */
  const handleStartTrial = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const data = await startTrial();
      if (data) {
        dispatch({
          type: SET_SUBSCRIPTION_STATUS,
          payload: normalizeSubscriptionStatus(data),
        });
      } else {
        await refreshStatus();
      }
      $crud.notify({
        type: "success",
        message: strings.kjuup_io_toast_trial_started || "Your free trial has started",
      });
      openIoSetupIfNeeded();
    } catch (e) {
      $crud.notify({
        type: "error",
        message: errorText(e, strings.kjuup_io_toast_trial_fail || strings.something_went_wrong),
      });
    } finally {
      setProcessing(false);
    }
  };

  /** Mid-trial or expired: collect card via subscribe endpoint. */
  const handleSubscribe = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      const data = await subscribe(selectedPlan);
      const {
        clientSecret,
        intentType,
        publishableKey,
        billingStartsAfterTrial,
        setupIntentId,
        subscriptionId,
      } = data || {};
      if (!clientSecret || !publishableKey) {
        throw new Error(strings.something_went_wrong || "Something went wrong");
      }
      const stripePromise = loadStripe(publishableKey);
      setCheckout({
        clientSecret,
        intentType: intentType || "payment",
        stripePromise,
        billingStartsAfterTrial: !!billingStartsAfterTrial || intentType === "setup",
        setupIntentId: setupIntentId || null,
        subscriptionId: subscriptionId || null,
      });
    } catch (e) {
      $crud.notify({ type: "error", message: errorText(e, strings.something_went_wrong) });
    } finally {
      setProcessing(false);
    }
  };

  const handleCheckoutSuccess = async () => {
    if (checkout?.billingStartsAfterTrial || checkout?.intentType === "setup") {
      if (checkout.setupIntentId) {
        try {
          const status = await confirmSetup({
            setupIntentId: checkout.setupIntentId,
            subscriptionId: checkout.subscriptionId,
          });
          if (status) {
            dispatch({
              type: SET_SUBSCRIPTION_STATUS,
              payload: normalizeSubscriptionStatus(status),
            });
          }
        } catch (e) {
          console.log("[subscription] confirmSetup:", e?.message);
          await refreshWithRetry((d) => d && d.hasPaymentMethod);
        }
      } else {
        await refreshWithRetry((d) => d && d.hasPaymentMethod);
      }
      $crud.notify({
        type: "success",
        message:
          strings.kjuup_io_toast_card_saved ||
          "Payment method saved. You'll be charged when the trial ends.",
      });
      await refreshStatus();
    } else {
      $crud.notify({ type: "success", message: strings.kjuup_io_toast_activating });
      const active = await refreshWithRetry((d) => d && d.active && !d.trialing);
      if (!active) {
        $crud.notify({ type: "success", message: strings.kjuup_io_toast_activating_soon });
      }
    }
    setCheckout(null);
    setProcessing(false);
    openIoSetupIfNeeded();
  };

  const handleCancel = async () => {
    setShowCancelModal(false);
    setProcessing(true);
    try {
      await cancelSubscription(false);
      $crud.notify({ type: "success", message: strings.kjuup_io_toast_cancel_period });
      await refreshStatus();
    } catch (e) {
      $crud.notify({ type: "error", message: errorText(e, strings.kjuup_io_toast_cancel_fail) });
    } finally {
      setProcessing(false);
    }
  };

  const handleSwitch = async (targetPlan) => {
    setSwitchTarget(null);
    if (processing) return;
    setProcessing(true);
    try {
      await switchPlan(targetPlan);
      $crud.notify({
        type: "success",
        message: isTrialing
          ? t(strings, "kjuup_io_billing_after_trial", {
              date: formatDate(subscription.trialEnd || subscription.currentPeriodEnd),
            })
          : t(strings, "kjuup_io_toast_switch_scheduled", { plan: planLabel(targetPlan) }),
      });
      await refreshStatus();
    } catch (e) {
      $crud.notify({ type: "error", message: errorText(e, strings.kjuup_io_toast_switch_fail) });
    } finally {
      setProcessing(false);
    }
  };

  const handleResume = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      await resumeSubscription();
      $crud.notify({ type: "success", message: strings.kjuup_io_toast_resumed });
      await refreshStatus();
    } catch (e) {
      $crud.notify({ type: "error", message: errorText(e, strings.kjuup_io_toast_resume_fail) });
    } finally {
      setProcessing(false);
    }
  };

  const handleUndoSwitch = async () => {
    if (processing) return;
    setProcessing(true);
    try {
      await cancelScheduledSwitch();
      $crud.notify({ type: "success", message: strings.kjuup_io_toast_undo });
      await refreshStatus();
    } catch (e) {
      $crud.notify({ type: "error", message: errorText(e, strings.kjuup_io_toast_undo_fail) });
    } finally {
      setProcessing(false);
    }
  };

  const afterTrialPrice = (plan) => {
    if (!plan || plan.amount == null) return "";
    const key =
      plan.interval === "year" ? "kjuup_io_after_trial_yearly" : "kjuup_io_after_trial_monthly";
    const list = listPlan(plan);
    if (!list) return t(strings, key, { price: priceMajor(plan) });
    return tNode(strings, key, {
      price: <DiscountedPrice net={priceMajor(plan)} list={priceMajor(list)} />,
    });
  };

  const renderStatusTag = () => {
    let text = strings.kjuup_io_status_active || "ACTIVE";
    let tone = "ok";
    // A pending cancellation outranks the trial badge: it is the state the
    // user has to act on before access ends.
    if (subscription.cancelAtPeriodEnd) {
      text = strings.kjuup_io_status_cancels_soon || "CANCELS SOON";
      tone = "warn";
    } else if (isTrialing) {
      text = strings.kjuup_io_status_trialing || "TRIAL";
      tone = "new";
    } else if (subscription.hasScheduledSwitch) {
      text = strings.kjuup_io_status_switch_scheduled || "SWITCH SCHEDULED";
      tone = "new";
    }
    return <span className={`kj-plan-tag is-${tone}`}>{text}</span>;
  };

  const renderActiveActions = () => {
    // Mid-trial with card: change plan without paid schedule (keeps trial).
    if (isTrialing) {
      const target = otherPlan(subscription.plan);
      // Canceling mid-trial also sets cancel_at_period_end, so the trial needs
      // the same Resume action as a paid plan — otherwise the only way back is
      // to let the subscription lapse.
      if (subscription.cancelAtPeriodEnd) {
        return (
          <>
            <button
              type="button"
              className="kjuup-sub-gradient-btn"
              disabled={processing}
              onClick={handleResume}
            >
              <span className="btn-inner">{strings.kjuup_io_resume}</span>
            </button>
            <button
              type="button"
              className="kj-btn kj-btn--outline kj-btn--email kj-btn--on-surface kj-btn--lg kj-btn--block kjuup-sub-outline-btn"
              disabled={processing}
              onClick={() => setSwitchTarget(target)}
            >
              {t(strings, "kjuup_io_switch_to_plan", { plan: planLabel(target) })}
              {priceByPlan[target] ? (
                <span className="price-pill">{compactPrice(priceByPlan[target])}</span>
              ) : null}
            </button>
          </>
        );
      }
      return (
        <>
          <button
            type="button"
            className="kjuup-sub-gradient-btn"
            disabled={processing}
            onClick={() => setSwitchTarget(target)}
          >
            <span className="btn-inner">
              {t(strings, "kjuup_io_switch_to_plan", { plan: planLabel(target) })}
              {priceByPlan[target] ? (
                <span className="price-pill">{compactPrice(priceByPlan[target])}</span>
              ) : null}
            </span>
          </button>
          <button
            type="button"
            className="kj-btn kj-btn--outline kj-btn--danger kj-btn--on-surface kj-btn--lg kj-btn--block kjuup-sub-outline-btn"
            disabled={processing}
            onClick={() => setShowCancelModal(true)}
          >
            {strings.kjuup_io_cancel_subscription}
          </button>
        </>
      );
    }

    if (subscription.cancelAtPeriodEnd) {
      return (
        <>
          <button
            type="button"
            className="kjuup-sub-gradient-btn"
            disabled={processing}
            onClick={handleResume}
          >
            <span className="btn-inner">{strings.kjuup_io_resume}</span>
          </button>
          {subscription.plan === "plus_monthly" ? (
            <button
              type="button"
              className="kj-btn kj-btn--outline kj-btn--email kj-btn--on-surface kj-btn--lg kj-btn--block kjuup-sub-outline-btn"
              disabled={processing}
              onClick={() => setSwitchTarget("plus_yearly")}
            >
              {t(strings, "kjuup_io_switch_to_plan", { plan: strings.kjuup_io_plan_yearly })}
              {priceByPlan.plus_yearly ? (
                <span className="price-pill">{compactPrice(priceByPlan.plus_yearly)}</span>
              ) : null}
            </button>
          ) : null}
        </>
      );
    }
    if (subscription.hasScheduledSwitch) {
      return (
        <>
          <button
            type="button"
            className="kj-btn kj-btn--outline kj-btn--email kj-btn--on-surface kj-btn--lg kj-btn--block kjuup-sub-outline-btn"
            disabled={processing}
            onClick={handleUndoSwitch}
          >
            {strings.kjuup_io_undo_switch}
          </button>
          <button
            type="button"
            className="kj-btn kj-btn--outline kj-btn--danger kj-btn--on-surface kj-btn--lg kj-btn--block kjuup-sub-outline-btn"
            disabled={processing}
            onClick={() => setShowCancelModal(true)}
          >
            {strings.kjuup_io_cancel_subscription}
          </button>
        </>
      );
    }
    const target = otherPlan(subscription.plan);
    return (
      <>
        <button
          type="button"
          className="kjuup-sub-gradient-btn"
          disabled={processing}
          onClick={() => setSwitchTarget(target)}
        >
          <span className="btn-inner">
            {t(strings, "kjuup_io_switch_to_plan", { plan: planLabel(target) })}
            {priceByPlan[target] ? (
              <span className="price-pill">{compactPrice(priceByPlan[target])}</span>
            ) : null}
          </span>
        </button>
        <button
          type="button"
          className="kj-btn kj-btn--outline kj-btn--danger kj-btn--on-surface kj-btn--lg kj-btn--block kjuup-sub-outline-btn"
          disabled={processing}
          onClick={() => setShowCancelModal(true)}
        >
          {strings.kjuup_io_cancel_subscription}
        </button>
      </>
    );
  };

  // Monthly / Yearly tiles: price with its period, the list price struck through.
  const planPrice = (key) => {
    const plan = priceByPlan[key];
    if (!plan || plan.amount == null) return null;
    const list = listPlan(plan);
    return (
      <>
        <span className="kj-plan-option__amount">
          <DiscountedPrice net={priceMajor(plan)} list={list ? priceMajor(list) : null} />
        </span>{" "}
        <span className="kj-plan-option__period">
          {plan.interval === "year"
            ? tr("pro_settings_plan_per_year", "/ Jahr", "/ year")
            : tr("pro_settings_plan_per_month", "/ Monat", "/ month")}
        </span>
      </>
    );
  };

  /** The running plan: status, Monthly / Yearly, details and the plan actions. */
  const renderIoActive = () => {
    const price = priceByPlan[subscription.plan];
    // The other tile opens the same switch confirmation as its button, and only while it is offered.
    const canSwitchTo = (key) => {
      if (key === subscription.plan) return false;
      if (isTrialing) return true;
      if (subscription.cancelAtPeriodEnd) return subscription.plan === PLAN_MONTHLY && key === PLAN_YEARLY;
      return !subscription.hasScheduledSwitch;
    };
    const rows = [
      { key: "plan", label: strings.kjuup_io_label_plan, value: planLabel(subscription.plan) },
      { key: "status", label: strings.kjuup_io_label_status, value: capitalize(subscription.subscriptionStatus) || "—" },
      {
        key: "date",
        label: isTrialing
          ? strings.kjuup_io_label_access_until || "Trial ends"
          : subscription.cancelAtPeriodEnd
            ? strings.kjuup_io_label_access_until
            : strings.kjuup_io_label_renews,
        value: formatDate(
          isTrialing ? subscription.trialEnd || subscription.currentPeriodEnd : subscription.currentPeriodEnd
        ),
      },
    ];
    return (
      <div className="kj-plan-io">
        <div className="kj-plan-io__status">
          {renderStatusTag()}
          {price ? (
            <span className="kj-plan-io__price">
              <DiscountedPrice net={formatPrice(price)} list={listPlan(price) ? formatPrice(listPlan(price)) : null} />
            </span>
          ) : null}
        </div>
        {!isTrialing && subscription.hasScheduledSwitch ? (
          <div className="kj-plan-banner is-new">
            <span className="material-symbols-outlined" aria-hidden="true">event_repeat</span>
            <span>
              {t(strings, "kjuup_io_switching_to", {
                plan: planLabel(subscription.pendingPlan),
                date: formatDate(subscription.pendingStartDate || subscription.currentPeriodEnd),
              })}
              {priceByPlan[subscription.pendingPlan] ? ` — ${formatPrice(priceByPlan[subscription.pendingPlan])}` : ""}
            </span>
          </div>
        ) : null}
        <PlanOptions
          label={strings.kjuup_io_billing_period_aria || "Billing period"}
          options={[PLAN_MONTHLY, PLAN_YEARLY].map((key) => ({
            key,
            label: planLabel(key),
            price: planPrice(key),
            selected: subscription.plan === key,
            disabled: processing,
            onClick: canSwitchTo(key) ? () => setSwitchTarget(key) : null,
          }))}
        />
        <div className="kj-plan-rows">
          {rows.map((row) => (
            <div key={row.key} className="kj-plan-row">
              <span className="kj-plan-row__label">{row.label}</span>
              <span className="kj-plan-row__value">{row.value}</span>
            </div>
          ))}
        </div>
        {discountText ? <p className="kj-plan-io__ok">{discountText}</p> : null}
        {isTrialing ? <TrialCountdownText className="kj-plan-io__countdown" /> : null}
        {isTrialing && hasPaymentMethod && !subscription.cancelAtPeriodEnd ? (
          <p className="kj-plan-io__text">
            {t(strings, "kjuup_io_billing_after_trial", {
              date: formatDate(subscription.trialEnd || subscription.currentPeriodEnd),
            })}
          </p>
        ) : null}
        {!subscription.hasScheduledSwitch && subscription.cancelAtPeriodEnd ? (
          <div className="kj-plan-banner is-warn">
            <span className="material-symbols-outlined" aria-hidden="true">event_busy</span>
            <span>{strings.kjuup_io_cancel_pending_note}</span>
          </div>
        ) : null}
        <div className="kjuup-sub-actions">{renderActiveActions()}</div>
      </div>
    );
  };

  /** No plan yet, a trial without a card, or an ended trial: pick Monthly / Yearly and book. */
  const renderIoSales = (view) => {
    const trialEndDate = subscription.trialEnd || subscription.currentPeriodEnd;
    const hero =
      view === "startTrial"
        ? {
            badge: strings.kjuup_io_trial_badge || "1 MONTH FREE TRIAL",
            sub:
              strings.kjuup_io_trial_trust ||
              "No payment today. No credit card details are requested before your paid subscription starts.",
          }
        : view === "trialSubscribe"
          ? {
              badge: strings.kjuup_io_status_trialing || "TRIAL",
              title: strings.kjuup_io_trial_active_title || "You're on a free trial",
              sub:
                strings.kjuup_io_trial_active_subtitle ||
                "Choose a plan and subscribe — billing starts when your trial ends.",
              countdown: true,
            }
          : {
              title: strings.kjuup_io_trial_ended_title || "Your free trial has ended",
              sub:
                strings.kjuup_io_trial_ended_body ||
                "Subscribe to Kjuup.io to keep using email and other premium features.",
            };
    const isYearly = selectedPlan === PLAN_YEARLY;
    const infoItems =
      view === "startTrial"
        ? [
            strings.kjuup_io_info_no_card ||
              "No credit card required – start immediately without entering payment details.",
            t(
              strings,
              "kjuup_io_info_days_free",
              { days: subscription.trialPeriodDays || DEFAULT_TRIAL_DAYS },
              "{days} days completely free – your subscription only starts after the trial."
            ),
            strings.kjuup_io_info_cancel_anytime || "Cancel anytime – no commitment, no risk.",
          ]
        : [];
    const cta =
      view === "startTrial"
        ? { label: strings.kjuup_io_start_free_trial || "Start free now – no risk", onClick: handleStartTrial }
        : { label: strings.kjuup_io_subscribe_now || "Subscribe now", onClick: handleSubscribe };
    return (
      <div className="kj-plan-io">
        <div className="kj-plan-io__hero">
          {hero.badge ? <span className="kj-plan-tag is-ok">{hero.badge}</span> : null}
          {hero.title ? <p className="kj-plan-io__title">{hero.title}</p> : null}
          {hero.sub ? <p className="kj-plan-io__text">{hero.sub}</p> : null}
          {hero.countdown ? <TrialCountdownText className="kj-plan-io__countdown" /> : null}
        </div>
        <div className="kj-plan-io__plans">
          <p className="kj-plan-io__caption">{strings.kjuup_io_choose_plan || "Choose your plan"}</p>
          <PlanOptions
            label={strings.kjuup_io_billing_period_aria || "Billing period"}
            options={[PLAN_MONTHLY, PLAN_YEARLY].map((key) => ({
              key,
              label: planLabel(key),
              price: planPrice(key),
              selected: selectedPlan === key,
              disabled: processing,
              onClick: () => selectPlan(key),
            }))}
          />
          {isYearly && pricing.yearlySavings ? (
            <p className="kj-plan-io__ok">
              {t(strings, "kjuup_io_save_amount_year", { price: pricing.yearlySavings }, "Save {price} per year")}
            </p>
          ) : null}
          {view !== "startTrial" && priceByPlan[selectedPlan] ? (
            <p className="kj-plan-io__small">{afterTrialPrice(priceByPlan[selectedPlan])}</p>
          ) : null}
          {discountText ? <p className="kj-plan-io__ok">{discountText}</p> : null}
        </div>
        {infoItems.length ? (
          <ul className="kj-plan-io__info">
            {infoItems.map((item) => (
              <li key={item}>
                <span className="material-symbols-outlined" aria-hidden="true">info</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <button type="button" className="kjuup-sub-gradient-btn" disabled={processing} onClick={cta.onClick}>
          <span className="btn-inner">{processing ? "…" : cta.label}</span>
        </button>
        {view === "trialSubscribe" && trialEndDate ? (
          <p className="kj-plan-io__small kj-plan-io__center">
            {t(
              strings,
              "kjuup_io_billing_after_trial",
              { date: formatDate(trialEndDate) },
              "You'll be charged after your trial ends on {date}."
            )}
          </p>
        ) : null}
        {view !== "startTrial" ? (
          <p className="kj-plan-io__secure">
            <span className="material-symbols-outlined" aria-hidden="true">lock</span>
            {strings.kjuup_io_secured_stripe}
          </p>
        ) : null}
      </div>
    );
  };

  const contactCompanyAdmin = (admin) => {
    const named = [admin.firstName, admin.lastName].filter(Boolean).join(" ").trim();
    openDirectChat(dispatch, history, {
      userId: admin.id,
      name: named || admin.name,
      avatar: admin.avatar,
      companyId: companyBilling && companyBilling.companyId,
      currentUserId: reduxUser && reduxUser.id,
      activityMessages,
    }).catch(() => {
      $crud.notify({
        type: "error",
        message: strings.something_went_wrong || "Something went wrong.",
      });
    });
  };

  /** Kjuup.io booked by the Kjuup+ company: seat state, the admins to contact, seat management for admins. */
  const renderIoCompany = () => {
    const trialEnded = shouldShowCompanyTrialEndedNote(subscription);
    const showAdminTrialUpgrade = shouldShowCompanyTrialUpgradeNotes(subscription);
    const days =
      subscription.daysRemaining != null
        ? Number(subscription.daysRemaining)
        : subscription.trialDaysRemaining != null
          ? Number(subscription.trialDaysRemaining)
          : null;
    const showTrialDays = isTrialing && days != null && Number.isFinite(days) && days <= 7;
    const adminNames = formatCompanyAdminNames(subscription.companyAdmins);
    const companyName = companyBilling && companyBilling.companyName;
    // Admins who hand out the seats manage them on the Kjuup.io settings page.
    const managesSeats = isCompanyAdminWithSeat(subscription) && !!(companyBilling && companyBilling.companyId);
    const admins = managesSeats
      ? []
      : (subscription.companyAdmins || []).filter(
          (admin) => admin && admin.id && String(admin.id) !== String((reduxUser && reduxUser.id) || "")
        );

    const title = trialEnded
      ? strings.kjuup_io_company_trial_ended_title || strings.kjuup_io_trial_ended_title || "Free trial ended!"
      : showAdminTrialUpgrade
        ? strings.kjuup_io || "Kjuup.io"
        : strings.kjuup_io_company_managed_title || "Managed by your company";

    const body = trialEnded
      ? companyTrialEndedAskBody(strings, subscription)
      : showAdminTrialUpgrade
        ? strings.kjuup_io_trial_card_subtitle || "Upgrade now to keep using all features."
        : managesSeats
          ? fillStr(
              tr("pro_settings_plan_io_admin_body", "Du verwaltest die Kjuup.io-Plätze von {{company}}.", "You manage the Kjuup.io seats of {{company}}."),
              { company: companyName || strings.kjuup_io_your_company || "your company" }
            )
          : (
              strings.kjuup_io_company_managed_body ||
              "Kjuup.io for your Kjuup+ company is booked and billed in the Admin Panel. Contact {admins} to get access."
            )
              .replace("{admins}", adminNames || strings.kjuup_io_your_company || "your company admin")
              .replace("{company}", companyName || strings.kjuup_io_your_company || "your company");

    return (
      <div className="kj-plan-io">
        <div className="kj-plan-io__hero">
          {companyName ? (
            <span className="kj-plan-tag is-muted">
              <span className="material-symbols-outlined" aria-hidden="true">domain</span>
              {companyName}
            </span>
          ) : null}
          <p className="kj-plan-io__title">{title}</p>
          <p className="kj-plan-io__text">{body}</p>
          {showTrialDays ? <TrialCountdownText as="p" className="kj-plan-io__countdown" /> : null}
          {isActive && !trialEnded && !showAdminTrialUpgrade ? (
            <span className="kj-plan-tag is-ok">
              <span className="material-symbols-outlined" aria-hidden="true">check_circle</span>
              {strings.kjuup_io_company_seat_active || "Your company seat is active."}
            </span>
          ) : null}
        </div>
        {showAdminTrialUpgrade ? (
          <button
            type="button"
            className="kjuup-sub-gradient-btn"
            onClick={() => navigateToKjuupIoBilling(history, subscription)}
          >
            <span className="btn-inner">{strings.kjuup_io_subscribe_now || "Subscribe now"}</span>
          </button>
        ) : managesSeats ? (
          <button
            type="button"
            className="kj-btn kj-btn--secondary kj-btn--lg kj-btn--block"
            onClick={() => navigateToKjuupIoBilling(history, subscription)}
          >
            <span className="material-symbols-outlined" aria-hidden="true">group</span>
            {tr("pro_settings_plan_manage_seats", "Plätze verwalten", "Manage seats")}
          </button>
        ) : null}
        {admins.length ? (
          <div className="kj-plan-io__admins">
            <p className="kj-plan-io__caption">{strings.kjuup_io_company_admins_title || "Your company admins"}</p>
            <p className="kj-plan-io__small">
              {strings.kjuup_io_company_admins_hint || "They can assign you a Kjuup.io seat in the Admin Panel."}
            </p>
            <ul className="kjuup-sub-admins">
              {admins.map((admin) => (
                <li key={admin.id} className="kjuup-sub-admin">
                  <AdminAvatar admin={admin} />
                  <div className="kjuup-sub-admin-text">
                    <span className="kjuup-sub-admin-name">{adminLabel(admin)}</span>
                    <span className="kjuup-sub-admin-role">
                      {strings.cockpit_company_role_super_admin || "Company admin"}
                    </span>
                  </div>
                  <button
                    type="button"
                    className="kj-btn kj-btn--outline kj-btn--email kj-btn--soft-hover kj-btn--on-surface kjuup-sub-admin-contact"
                    onClick={() => contactCompanyAdmin(admin)}
                  >
                    <span className="material-symbols-outlined" aria-hidden="true">
                      chat
                    </span>
                    {strings.cockpit_io_contact_admin || strings.contact || "Contact"}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    );
  };

  /** Body of the Kjuup.io card, by subscription state. */
  const renderIoBody = () => {
    if (screenLoading) {
      return (
        <div className="kj-plan-bones" aria-hidden="true">
          <span className="kj-plan-bone kj-plan-bone--line" />
          <div className="kj-plan-options">
            <span className="kj-plan-bone kj-plan-bone--tile" />
            <span className="kj-plan-bone kj-plan-bone--tile" />
          </div>
          <span className="kj-plan-bone kj-plan-bone--button" />
        </div>
      );
    }
    // Kjuup+ company members: personal Stripe booking is disabled.
    if (isKjuupPlusMember) return renderIoCompany();
    // Trial running, no card yet → choose Monthly/Yearly + Subscribe now
    if (isTrialing && !hasPaymentMethod) return renderIoSales("trialSubscribe");
    // Paid active, or trialing with card already on file → manage UI
    if (isActive) return renderIoActive();
    if (canStartTrial) return renderIoSales("startTrial");
    return renderIoSales("expired");
  };

  // Plans (the app's Plans screen): one card per package, "Current" on the user's.
  const currentLabel = tr("pro_settings_plan_current", "Aktuell", "Current");
  const switchLabel = (pkg) => fillStr(tr("pro_settings_plan_switch", "Zu {{pkg}} wechseln", "Switch to {{pkg}}"), { pkg });
  // Back to Kjuup = ending the personal Kjuup.io plan (the cancel confirmation).
  const canCancelPersonal =
    !screenLoading && !isKjuupPlusMember && isActive && !(isTrialing && !hasPaymentMethod) && !subscription.cancelAtPeriodEnd;

  return (
    <div className="company_section company_list setting_profile kjuup-sub-page kj-plans-page">
      <div className="page-header kj-plans-header">
        <button
          type="button"
          className="page-header-back kj-btn kj-btn--icon kj-btn--neutral"
          aria-label={tr("back", "Zurück", "Back")}
          onClick={goBack}
        >
          <span className="material-symbols-outlined kjuup-sub-back-icon" aria-hidden="true">
            keyboard_arrow_left
          </span>
        </button>
        <h2 className="page-header-title">{tr("pro_settings_plans", "Pakete", "Plans")}</h2>
      </div>

      <div className="kj-plans-scroll">
        <div className="kj-plans">
          <PlanCard
            id="kjuup"
            label="Kjuup"
            tag={tr("pro_settings_plan_kjuup_tag", "Chat, Projekte und Aufgaben", "Chat, projects and tasks")}
            current={pkgId === "kjuup"}
            currentLabel={currentLabel}
            features={[
              tr("pro_settings_plan_kjuup_f1", "Projekte und Chats", "Projects and chats"),
              tr("pro_settings_plan_kjuup_f2_priorities", "Aufgaben inkl. vorgemerkter Aufgaben", "ToDo and bookmarked tasks"),
            ]}
          >
            {pkgId !== "kjuup" && canCancelPersonal ? (
              <button
                type="button"
                className="kj-plan-switch"
                disabled={processing}
                onClick={() => setShowCancelModal(true)}
              >
                {switchLabel("Kjuup")}
              </button>
            ) : null}
          </PlanCard>

          <PlanCard
            id="plus"
            label="Kjuup+"
            tag={tr("pro_settings_plan_plus_tag", "Für wachsende Firmen", "For growing companies")}
            current={pkgId === "plus"}
            currentLabel={currentLabel}
            features={plusFeatures}
          >
            {pkgId !== "plus" && !plusMember ? (
              <a className="kj-plan-switch" href={appConfig.KJUUP_PLUS_ANFRAGE_URL} target="_blank" rel="noopener noreferrer">
                {switchLabel("Kjuup+")}
              </a>
            ) : pkgId !== "plus" ? (
              <p className="kj-plan-card__note">
                <span className="material-symbols-outlined" aria-hidden="true">domain</span>
                {tr("pro_settings_plan_plus_note", "Kjuup+ wird von deiner Firma gebucht.", "Kjuup+ is booked by your company.")}
              </p>
            ) : null}
          </PlanCard>

          <PlanCard
            id="io"
            label="Kjuup.io"
            tag={tr("pro_settings_plan_io_tag_v2", "E-Mail, Tagesbericht und Automatisierungen", "E-mail, daily report and automations")}
            current={pkgId === "io"}
            currentLabel={currentLabel}
            features={screenLoading ? [] : ioFeatureLabels}
          >
            {renderIoBody()}
          </PlanCard>
        </div>
      </div>

      {checkout ? (
        <div className="kjuup-sub-modal-overlay">
          <div className="kjuup-sub-modal-card">
            <div className="kjuup-sub-modal-title">
              {strings.kjuup_io_subscribe_now || "Subscribe now"}
            </div>
            <Elements
              stripe={checkout.stripePromise}
              options={{
                clientSecret: checkout.clientSecret,
                appearance: {
                  theme: "stripe",
                  variables: { colorPrimary: "#e24bf5" },
                },
              }}
            >
              <CheckoutForm
                intentType={checkout.intentType}
                onSuccess={handleCheckoutSuccess}
                onCancel={() => setCheckout(null)}
                strings={strings}
                processing={processing}
                setProcessing={setProcessing}
                ctaLabel={strings.kjuup_io_subscribe_now || "Subscribe now"}
              />
            </Elements>
          </div>
        </div>
      ) : null}

      {switchTarget ? (
        <div className="kjuup-sub-modal-overlay">
          <div className="kjuup-sub-modal-card">
            <div className="kjuup-sub-modal-title">
              {t(strings, "kjuup_io_switch_modal_title", { plan: planLabel(switchTarget) })}
            </div>
            {priceByPlan[switchTarget] ? (
              <div className="kjuup-sub-price-summary">
                <div>
                  <div className="kjuup-sub-price-summary-plan">
                    {strings.kjuup_io} — {planLabel(switchTarget)}
                  </div>
                  <div className="kjuup-sub-price-summary-interval">
                    {priceByPlan[switchTarget].interval === "year"
                      ? strings.kjuup_io_billed_yearly
                      : strings.kjuup_io_billed_monthly}
                  </div>
                </div>
                <div className="kjuup-sub-price-summary-amount">{formatPrice(priceByPlan[switchTarget])}</div>
              </div>
            ) : null}
            <p className="kjuup-sub-modal-note">
              {isTrialing
                ? t(strings, "kjuup_io_billing_after_trial", {
                    date: formatDate(subscription.trialEnd || subscription.currentPeriodEnd),
                  })
                : t(strings, "kjuup_io_switch_modal_note", {
                    date: formatDate(subscription.currentPeriodEnd),
                    plan: planLabel(subscription.plan),
                  })}
            </p>
            <button
              type="button"
              className="kjuup-sub-gradient-btn"
              onClick={() => handleSwitch(switchTarget)}
            >
              <span className="btn-inner">{strings.kjuup_io_confirm_switch}</span>
            </button>
            <button
              type="button"
              className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--block"
              onClick={() => setSwitchTarget(null)}
            >
              {strings.kjuup_io_not_now}
            </button>
          </div>
        </div>
      ) : null}

      {showCancelModal ? (
        <div className="kjuup-sub-modal-overlay">
          <div className="kjuup-sub-modal-card">
            <div className="kjuup-sub-modal-title">{strings.kjuup_io_cancel_modal_title}</div>
            <p className="kjuup-sub-modal-note">
              {subscription.currentPeriodEnd
                ? t(strings, "kjuup_io_cancel_modal_note_with_date", {
                    date: formatDate(subscription.currentPeriodEnd),
                  })
                : strings.kjuup_io_cancel_modal_note}
            </p>
            <button type="button" className="kj-btn kj-btn--primary kj-btn--danger kj-btn--lg kj-btn--block" onClick={handleCancel}>
              {strings.kjuup_io_cancel_plan}
            </button>
            <button
              type="button"
              className="kj-btn kj-btn--ghost kj-btn--neutral kj-btn--block"
              onClick={() => setShowCancelModal(false)}
            >
              {strings.kjuup_io_keep_subscription}
            </button>
          </div>
        </div>
      ) : null}
      <KjuupIoSetupModal open={showIoSetup} onClose={() => setShowIoSetup(false)} />
    </div>
  );
}
